import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { ShareNetwork, CircleNotch, Wrench, PaintRoller, Hammer, Ladder, Plugs } from "@phosphor-icons/react";
import { formatBRL, fileUrl } from "../lib/api";
import { AchievementIcon } from "../lib/achievementIcons";
import { shareImageFile } from "../lib/native";

// Pluralização correta com base na contagem (1 = singular, ≠1 = plural).
const plural = (n, sing, plur) => `${n} ${Number(n) === 1 ? sing : plur}`;

// V14.9: texto de compartilhamento dinâmico — destaca ganho de dinheiro quando faz sentido.
function buildShareText(a, code, link) {
  const label = a?.label || "uma conquista";
  const n = a?.count || 0;
  const cta = code ? ` Baixe o app e use meu código ${code}.` : " Baixe o AlugaJá!";
  const url = link ? ` ${link}` : "";
  const money = "💰 Já estou ganhando dinheiro com o AlugaJá — baixe agora e ganhe também!";
  switch (a?.challenge_key) {
    case "referral":
      return `🤑 Conquistei "${label}" indicando amigos no AlugaJá! ${money}${cta}${url}`;
    case "tools_rented_out":
      return `🏆 Conquistei o título de "${label}"! Já realizei a locação de ${plural(n, "item", "itens")} pelo AlugaJá. ${money}${cta}${url}`;
    case "total_earned_renting":
    case "freight_value":
    case "freight_count":
      return `🏆 Conquistei "${label}" no AlugaJá! ${money}${cta}${url}`;
    case "listings_created":
      return `🏆 Conquistei "${label}" no AlugaJá! Já tenho ${plural(n, "anúncio", "anúncios")} para alugar e gerar renda.${cta}${url}`;
    case "rentals_made":
      return `🏆 Conquistei "${label}" no AlugaJá! Já aluguei ${plural(n, "item", "itens")} com praticidade e segurança.${cta}${url}`;
    default:
      return `🏆 Desbloqueei "${label}" no AlugaJá! Alugue ferramentas e equipamentos perto de você.${cta}${url}`;
  }
}

const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";

// V15.0: descrição contextual exibida na imagem, abaixo do nome da conquista.
function buildDescription(a) {
  const n = a?.count || 0;
  switch (a?.challenge_key) {
    case "referral": return `Já indiquei ${plural(n, "amigo", "amigos")} que entraram no AlugaJá.`;
    case "tools_rented_out": return `Já realizei a locação de ${plural(n, "item", "itens")} pelo app.`;
    case "listings_created": return `Já cadastrei ${plural(n, "item", "itens")} no meu catálogo.`;
    case "rentals_made": return `Já aluguei ${plural(n, "item", "itens")} pelo AlugaJá.`;
    case "total_earned_renting": return `Já faturei alugando meus itens no AlugaJá.`;
    case "freight_count": return `Já realizei ${plural(n, "frete/entrega", "fretes/entregas")} pelo app.`;
    case "freight_value": return `Já movimentei valor em fretes no AlugaJá.`;
    default: return a?.challenge_title || "";
  }
}

export default function ShareAchievement({ achievement, code, link, userName, userAvatar, className = "", compact = false }) {
  const storyRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const color = achievement?.color || "#FF6B35";
  const avatarSrc = userAvatar ? fileUrl(userAvatar) : null;

  const handleShare = async (e) => {
    e?.stopPropagation?.();
    if (!storyRef.current || !achievement || sharing) return;
    setSharing(true);
    try {
      // Deixa o spinner pintar antes do html2canvas (que bloqueia a thread principal).
      await new Promise((r) => setTimeout(r, 60));
      const canvas = await html2canvas(storyRef.current, { backgroundColor: null, scale: 1, useCORS: true, logging: false });
      const text = buildShareText(achievement, code, link);
      const dataUrl = canvas.toDataURL("image/png");
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
      const res = await shareImageFile({ blob, dataUrl, text, url: link, title: "AlugaJá", fileName: "alugaja-conquista.png" });
      if (res.method === "download" && res.ok) {
        try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
        toast.success("Imagem salva! Compartilhe no seu story 📲", { duration: 2500 });
      } else if (!res.ok && res.method === "none") {
        toast.error("Não foi possível abrir o compartilhamento.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Não foi possível gerar a imagem.");
    } finally {
      setSharing(false);
    }
  };

  const toolIcons = [Wrench, PaintRoller, Hammer, Ladder, Plugs];

  return (
    <>
      <button onClick={handleShare} disabled={sharing} className={className} data-testid={`share-achievement-${achievement?.challenge_key}-${achievement?.count}`}>
        {sharing
          ? <CircleNotch size={compact ? 14 : 18} weight="bold" className="animate-spin" />
          : <ShareNetwork size={compact ? 14 : 18} weight="bold" />}
        {!compact && <span>{sharing ? "Gerando..." : "Compartilhar"}</span>}
      </button>

      {/* Story card oculto (1080x1920) */}
      <div style={{ position: "fixed", left: "-99999px", top: 0, pointerEvents: "none" }} aria-hidden>
        <div ref={storyRef} style={{
          width: 1080, height: 1920, background: "linear-gradient(165deg, #0F172A 0%, #1E293B 50%, #004E98 130%)",
          color: "#fff", display: "flex", flexDirection: "column", alignItems: "center",
          fontFamily: "Inter, Arial, sans-serif", padding: 72, boxSizing: "border-box", textAlign: "center",
        }}>
          {/* Cabeçalho pessoal: foto + Parabéns + nome */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
            <div style={{
              width: 180, height: 180, borderRadius: "50%", overflow: "hidden", border: `8px solid ${color}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "#1E293B", boxShadow: `0 0 60px ${color}66`,
            }}>
              {avatarSrc ? (
                <img src={avatarSrc} alt="" crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 90, fontWeight: 900, color: "#fff" }}>{(userName || "A")[0]?.toUpperCase()}</span>
              )}
            </div>
            <div style={{ fontSize: 44, fontWeight: 900, color: "#FFD166" }}>🎉 Parabéns, {firstName(userName) || "você"}!</div>
          </div>

          <div style={{ fontSize: 42, fontWeight: 900, color: "#fff", marginTop: 44, textTransform: "uppercase", letterSpacing: 3 }}>
            Conquista desbloqueada
          </div>

          {/* Medalha */}
          <div style={{ position: "relative", width: 360, height: 360, margin: "40px 0 24px" }}>
            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: `radial-gradient(circle, ${color}66, transparent 70%)` }} />
            <div style={{ position: "absolute", inset: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                          background: `radial-gradient(circle at 35% 30%, ${color}, ${color}AA)`, border: "10px solid #ffffff44" }}>
              <AchievementIcon icon={achievement?.icon} size={180} style={{ color: "#fff" }} />
            </div>
          </div>
          <div style={{ fontSize: 30, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>{achievement?.challenge_title}</div>
          <div style={{ fontSize: 84, fontWeight: 900, lineHeight: 1.05, marginTop: 8 }}>{achievement?.label}</div>
          <div style={{ fontSize: 32, fontWeight: 600, color: "rgba(255,255,255,0.82)", marginTop: 16, maxWidth: 820 }}>{buildDescription(achievement)}</div>
          {achievement?.reward > 0 && (
            <div style={{ marginTop: 28, fontSize: 44, fontWeight: 900, color: "#16A34A", background: "#ECFDF5", borderRadius: 24, padding: "14px 36px" }}>
              + {formatBRL(achievement.reward)}
            </div>
          )}

          {/* Rodapé: ícones de ferramentas + CTA */}
          <div style={{ marginTop: "auto", width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 28, marginBottom: 36, opacity: 0.9 }}>
              {toolIcons.map((Icon, i) => (
                <div key={i} style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={44} weight="fill" color="#FF6B35" />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 46, fontWeight: 900 }}>📲 Baixe o <span style={{ color: "#FF6B35" }}>AlugaJá</span></div>
            <div style={{ fontSize: 32, color: "rgba(255,255,255,0.8)", marginTop: 14 }}>
              Alugue e anuncie ferramentas perto de você
            </div>
            {code && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 34 }}>
                <div style={{ background: "#FF6B35", borderRadius: 20, padding: "18px 40px", fontSize: 38, fontWeight: 900, textAlign: "center" }}>
                  Use meu código: {code}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
